import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useSite } from "@/App";
import { NAV_ITEMS, CONTACT, LINKS } from "./data";

export default function Navbar() {
  const { scrollToId, openQuote, openExpert } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    NAV_ITEMS.forEach((n) => {
      const el = document.getElementById(n.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    scrollToId(id);
    setOpen(false);
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 border-b ${
          scrolled
            ? "bg-[#101317]/85 backdrop-blur-xl border-[#9AA3AF]/15"
            : "bg-transparent border-transparent"
        }`}
      >
        <nav className="max-w-[1400px] mx-auto px-5 lg:px-10 h-20 flex items-center justify-between gap-4">
          {/* Logo */}
          <button
            data-testid="nav-logo"
            onClick={() => go("home")}
            className="flex items-center gap-3 shrink-0 focus-gold rounded-md"
          >
            <img
              src="/logo.png"
              alt="Million-Air A/C & Heating"
              className="h-10 md:h-12 w-auto"
            />
          </button>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-link-${n.id}`}
                onClick={() => go(n.id)}
                className={`relative text-sm tracking-wide transition-colors duration-300 ${
                  active === n.id ? "text-[#C9A44C]" : "text-[#9AA3AF] hover:text-white"
                }`}
              >
                {n.label}
                {active === n.id && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#C9A44C]"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden xl:flex items-center gap-2.5 shrink-0">
            <a
              data-testid="nav-cta-financing"
              href={LINKS.FINANCING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="focus-gold px-4 py-2.5 rounded-full text-xs font-semibold bg-[#C3262E] text-white inline-flex items-center gap-2 hover:bg-[#d83a42] transition-colors duration-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Financing Available
            </a>
            <button
              data-testid="nav-cta-expert"
              onClick={openExpert}
              className="focus-gold px-4 py-2.5 rounded-full text-xs font-semibold bg-[#005EA8] text-white hover:bg-[#0a6fbf] transition-colors duration-300"
            >
              Ask the Expert
            </button>
            <button
              data-testid="nav-cta-quote"
              onClick={openQuote}
              className="focus-gold px-5 py-2.5 rounded-full text-xs font-bold bg-[#C9A44C] text-[#171B22] shadow-[0_6px_20px_rgba(201,164,76,0.35)] hover:bg-[#D6B15A] transition-colors duration-300"
            >
              Request a Quote
            </button>
          </div>

          {/* Mobile: persistent quote + menu */}
          <div className="flex xl:hidden items-center gap-2">
            <button
              data-testid="nav-mobile-quote"
              onClick={openQuote}
              className="focus-gold px-4 py-2 rounded-full text-xs font-bold bg-[#C9A44C] text-[#171B22]"
            >
              Request a Quote
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              className="focus-gold p-2 rounded-md text-[#C9A44C] border border-[#9AA3AF]/25"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 xl:hidden bg-[#101317]/97 backdrop-blur-xl pt-24 px-6"
          >
            <div className="flex flex-col gap-6">
              <img src="/markonlyvector.png" alt="" className="h-14 w-auto mx-auto mb-2 opacity-90" />
              {NAV_ITEMS.map((n, i) => (
                <motion.button
                  key={n.id}
                  data-testid={`mobile-link-${n.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(n.id)}
                  className={`text-left text-3xl border-b border-[#9AA3AF]/15 pb-3 ${
                    active === n.id ? "text-[#C9A44C]" : "text-white"
                  }`}
                >
                  {n.label}
                </motion.button>
              ))}

              <div className="flex flex-col gap-3 mt-4">
                <button
                  data-testid="mobile-cta-expert"
                  onClick={() => { openExpert(); setOpen(false); }}
                  className="px-6 py-3.5 rounded-full font-semibold bg-[#005EA8] text-white"
                >
                  Ask the Expert
                </button>
                <a
                  data-testid="mobile-cta-financing"
                  href={LINKS.FINANCING_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3.5 rounded-full font-semibold bg-[#C3262E] text-white text-center inline-flex items-center justify-center gap-2"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  Financing Available
                </a>
                <a
                  href={CONTACT.phoneHref}
                  className="px-6 py-3.5 rounded-full font-semibold border border-[#C9A44C] text-[#C9A44C] text-center inline-flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> {CONTACT.phone}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
